/**
 * product.js
 * Variant Picker, Product Form (Ajax Add-to-Cart) und Media Gallery.
 *
 * Preis- und Button-Zustände werden bei Variantenwechsel über die
 * Section Rendering API neu vom Server geholt – Liquid bleibt die einzige
 * Quelle für Preisformatierung und Verfügbarkeitstexte.
 */

/* --------------------------------------------------------------------------
 * Variant Picker
 *
 * <variant-picker data-section="..." data-url="/products/handle">
 *   <select data-option-index="0">…</select>
 *   <script type="application/json" data-variants>[…]</script>
 * ------------------------------------------------------------------------ */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-variants]');
    this.variants = json ? JSON.parse(json.textContent) : [];
    this.addEventListener('change', () => this.onVariantChange());
  }

  get selectedOptions() {
    return Array.from(this.querySelectorAll('[data-option-index]'))
      .sort((a, b) => a.dataset.optionIndex - b.dataset.optionIndex)
      .map((select) => select.value);
  }

  get currentVariant() {
    const selected = this.selectedOptions;
    return this.variants.find((variant) =>
      variant.options.every((option, index) => option === selected[index])
    );
  }

  onVariantChange() {
    const variant = this.currentVariant;

    if (!variant) {
      this.setUnavailable();
      return;
    }

    this.updateFormInput(variant);
    this.updateUrl(variant);
    this.updateMedia(variant);
    this.refreshFromServer(variant);
  }

  updateFormInput(variant) {
    const form = document.getElementById(`ProductForm-${this.dataset.section}`);
    const input = form?.querySelector('input[name="id"]');
    if (input) {
      input.value = variant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  updateUrl(variant) {
    if (!this.dataset.url || window.location.pathname !== this.dataset.url) return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${variant.id}`);
  }

  updateMedia(variant) {
    if (!variant.featured_media) return;
    const gallery = document.getElementById(`MediaGallery-${this.dataset.section}`);
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(String(variant.featured_media.id));
    }
  }

  /**
   * Holt Preis und Buy-Buttons der Section für die gewählte Variante neu.
   */
  async refreshFromServer(variant) {
    try {
      const url = `${this.dataset.url}?variant=${variant.id}&section_id=${this.dataset.section}`;
      const response = await fetch(url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');

      [
        `Price-${this.dataset.section}`,
        `TotalPrice-${this.dataset.section}`,
        `ProductButtons-${this.dataset.section}`,
      ].forEach((id) => {
        const source = html.getElementById(id);
        const destination = document.getElementById(id);
        if (source && destination) destination.innerHTML = source.innerHTML;
      });
    } catch (error) {
      console.error('[product] variant refresh failed', error);
    }
  }

  setUnavailable() {
    const buttons = document.getElementById(`ProductButtons-${this.dataset.section}`);
    const submitButton = buttons?.querySelector('[type="submit"]');
    if (submitButton) {
      submitButton.setAttribute('disabled', '');
      const label = submitButton.querySelector('span');
      if (label) label.textContent = theme.strings.unavailable;
    }
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}

/* --------------------------------------------------------------------------
 * Product Form – Ajax Add-to-Cart mit Cart-Drawer-Update
 * ------------------------------------------------------------------------ */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    this.form?.addEventListener('submit', (event) => this.onSubmit(event));
  }

  get errorElement() {
    return this.querySelector('[data-form-error]');
  }

  async onSubmit(event) {
    event.preventDefault();

    const submitButton = this.form.querySelector('[type="submit"]');
    if (submitButton?.hasAttribute('disabled')) return;

    submitButton?.setAttribute('aria-busy', 'true');
    this.errorElement?.setAttribute('hidden', '');

    /* Mit data-redirect-to-cart geht es nach dem Hinzufügen direkt auf die
       Warenkorb-Seite statt in den Drawer (Produktseite: "Weiter"). */
    const drawer = this.hasAttribute('data-redirect-to-cart')
      ? null
      : document.getElementById('CartDrawer');
    const formData = new FormData(this.form);
    if (drawer) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
    }

    try {
      const response = await fetch(`${theme.routes.cartAddUrl}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });
      const result = await response.json();

      if (!response.ok) {
        this.showError(result.description || result.message || '');
        return;
      }

      const cart = await (await fetch(`${theme.routes.cartUrl}.js`)).json();
      theme.cart.updateCount(cart.item_count);
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));

      if (drawer) {
        drawer.renderContents(result.sections?.['cart-drawer']);
        drawer.open(submitButton);
      } else {
        window.location.href = theme.routes.cartUrl;
      }
    } catch (error) {
      console.error('[product] add to cart failed', error);
    } finally {
      submitButton?.removeAttribute('aria-busy');
    }
  }

  showError(message) {
    if (this.errorElement && message) {
      this.errorElement.textContent = message;
      this.errorElement.removeAttribute('hidden');
    }
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

/* --------------------------------------------------------------------------
 * Media Gallery – Thumbnails, Punkte-Navigation und Wischgeste
 * ------------------------------------------------------------------------ */

class MediaGallery extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('[data-media-item]'));

    this.addEventListener('click', (event) => {
      const control = event.target.closest('button[data-media-id]');
      if (control) this.setActiveMedia(control.dataset.mediaId);
    });

    this.initSwipe();
  }

  get activeIndex() {
    return this.items.findIndex((item) => !item.hidden);
  }

  setActiveMedia(mediaId) {
    this.items.forEach((item) => {
      item.hidden = item.dataset.mediaId !== mediaId;
    });
    this.querySelectorAll('button[data-media-id]').forEach((control) => {
      const isActive = control.dataset.mediaId === mediaId;
      if (isActive) {
        control.setAttribute('aria-current', 'true');
      } else {
        control.removeAttribute('aria-current');
      }
    });
    this.querySelector(`.product__thumbnail[data-media-id="${mediaId}"]`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
    });
  }

  showByOffset(offset) {
    if (this.items.length < 2) return;
    const index = (this.activeIndex + offset + this.items.length) % this.items.length;
    this.setActiveMedia(this.items[index].dataset.mediaId);
  }

  initSwipe() {
    const main = this.querySelector('.product__media-main');
    if (!main || this.items.length < 2) return;

    let startX = null;
    main.addEventListener('pointerdown', (event) => {
      startX = event.clientX;
    });
    main.addEventListener('pointerup', (event) => {
      if (startX === null) return;
      const delta = event.clientX - startX;
      startX = null;
      if (Math.abs(delta) > 40) this.showByOffset(delta < 0 ? 1 : -1);
    });
    main.addEventListener('pointercancel', () => {
      startX = null;
    });
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

/* --------------------------------------------------------------------------
 * Delivery Picker – wählbare Liefertage mit Bestellschluss-Countdown
 *
 * <delivery-picker data-cutoff="14" data-lead-days="2" data-day-count="10">
 *   <div data-days></div>
 *   <button data-scroll></button>
 *   <p data-note hidden></p>
 *   <input data-delivery-input>
 * ------------------------------------------------------------------------ */

const WEEKDAYS_SHORT = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
const WEEKDAYS_LONG = [
  'Sonntag',
  'Montag',
  'Dienstag',
  'Mittwoch',
  'Donnerstag',
  'Freitag',
  'Samstag',
];

class DeliveryPicker extends HTMLElement {
  connectedCallback() {
    this.daysContainer = this.querySelector('[data-days]');
    this.note = this.querySelector('[data-note]');
    this.input = this.querySelector('[data-delivery-input]');
    this.cutoff = Number(this.dataset.cutoff || 14);
    this.leadDays = Number(this.dataset.leadDays || 2);
    this.dayCount = Number(this.dataset.dayCount || 10);
    this.selectedKey = null;

    if (!this.daysContainer) return;

    this.backArrow = this.querySelector('[data-scroll-back]');
    this.forwardArrow = this.querySelector('[data-scroll]');
    this.forwardArrow?.addEventListener('click', () => this.scrollDays(1));
    this.backArrow?.addEventListener('click', () => this.scrollDays(-1));
    this.daysContainer.addEventListener('scroll', () => this.updateArrows(), { passive: true });

    this.render();
    this.timer = setInterval(() => this.tick(), 30000);
  }

  scrollDays(direction) {
    this.daysContainer.scrollBy({ left: this.daysContainer.clientWidth * 0.8 * direction });
  }

  /** Zurück-Pfeil erscheint erst nach dem Scrollen, vorwärts endet am Reihenende. */
  updateArrows() {
    const maxScroll = this.daysContainer.scrollWidth - this.daysContainer.clientWidth;
    const position = this.daysContainer.scrollLeft;
    if (this.backArrow) this.backArrow.hidden = position <= 4;
    if (this.forwardArrow) this.forwardArrow.disabled = position >= maxScroll - 4;
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** Bestellschluss: heute um X Uhr, danach morgen um X Uhr. */
  get deadline() {
    const deadline = new Date();
    deadline.setHours(this.cutoff, 0, 0, 0);
    if (deadline <= new Date()) deadline.setDate(deadline.getDate() + 1);
    return deadline;
  }

  /** Liefertage ab Bestellschluss plus Vorlauf, sonntags keine Zustellung. */
  get deliveryDays() {
    const days = [];
    const start = new Date(this.deadline);
    for (let i = 0; i < this.leadDays; i += 1) {
      start.setDate(start.getDate() + 1);
      if (start.getDay() === 0) start.setDate(start.getDate() + 1);
    }
    const cursor = new Date(start);
    while (days.length < this.dayCount) {
      if (cursor.getDay() !== 0) days.push(new Date(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return days;
  }

  formatKey(date) {
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  }

  formatIso(date) {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  formatShort(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${WEEKDAYS_LONG[date.getDay()]}, ${day}.${month}.`;
  }

  formatFull(date) {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${WEEKDAYS_LONG[date.getDay()]}, ${day}.${month}.${date.getFullYear()}`;
  }

  render() {
    const days = this.deliveryDays;
    const previous = this.selectedKey;
    this.daysContainer.innerHTML = '';

    days.forEach((date, index) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'delivery-day';
      button.dataset.key = this.formatKey(date);
      button.setAttribute('aria-pressed', 'false');
      button.innerHTML =
        `<span class="delivery-day__weekday">${WEEKDAYS_SHORT[date.getDay()]}</span>` +
        `<span class="delivery-day__date">${date.getDate()}</span>`;
      button.setAttribute('aria-label', `Liefertag ${this.formatShort(date)} wählen`);
      button.addEventListener('click', () => this.select(index));
      this.daysContainer.append(button);
    });

    this.appendCalendarTile(days[0]);
    this.updateArrows();

    this.dates = days;
    if (this.customDate && this.formatKey(this.customDate) === previous) {
      this.selectCustom(this.formatIso(this.customDate));
      return;
    }
    this.customDate = null;
    const restored = days.findIndex((date) => this.formatKey(date) === previous);
    this.select(restored >= 0 ? restored : 0);
  }

  /** Letzte Kachel der Reihe: öffnet den nativen Kalender für ein freies Datum. */
  appendCalendarTile(earliest) {
    const tile = document.createElement('span');
    tile.className = 'delivery-day delivery-day--calendar';
    tile.setAttribute('aria-pressed', 'false');
    tile.innerHTML =
      '<svg class="icon" viewBox="0 0 24 24" aria-hidden="true" focusable="false">' +
      '<rect x="4" y="6" width="16" height="14" rx="2"/>' +
      '<path d="M4 10h16M8 4v4M16 4v4"/></svg>' +
      '<span class="delivery-day__weekday">Kalender</span>';

    const picker = document.createElement('input');
    picker.type = 'date';
    picker.className = 'delivery-day__picker';
    picker.min = this.formatIso(earliest);
    const horizon = new Date(earliest);
    horizon.setFullYear(horizon.getFullYear() + 1);
    picker.max = this.formatIso(horizon);
    picker.setAttribute('aria-label', 'Liefertag im Kalender wählen');
    picker.addEventListener('click', () => {
      try {
        picker.showPicker();
      } catch (error) {
        /* Ohne showPicker öffnet der Browser den Kalender über den Fokus selbst. */
      }
    });
    picker.addEventListener('change', () => this.selectCustom(picker.value));

    tile.append(picker);
    this.daysContainer.append(tile);
    this.calendarTile = tile;
  }

  select(index) {
    const date = this.dates[index];
    if (!date) return;
    this.customDate = null;
    this.selectedKey = this.formatKey(date);

    this.daysContainer.querySelectorAll('.delivery-day').forEach((button) => {
      button.setAttribute(
        'aria-pressed',
        button.dataset.key === this.selectedKey ? 'true' : 'false'
      );
    });

    if (this.input) this.input.value = this.formatFull(date);
    this.updateNote(index === 0);
  }

  /** Frei gewähltes Datum aus dem Kalender, sonntags rutscht es auf den Montag. */
  selectCustom(value) {
    if (!value) return;
    const [year, month, day] = value.split('-').map(Number);
    let date = new Date(year, month - 1, day);
    const earliest = this.deliveryDays[0];
    if (date < earliest) date = new Date(earliest);

    let sundayMoved = false;
    if (date.getDay() === 0) {
      date.setDate(date.getDate() + 1);
      sundayMoved = true;
    }

    const key = this.formatKey(date);
    const visibleIndex = this.dates.findIndex((d) => this.formatKey(d) === key);
    if (visibleIndex >= 0 && !sundayMoved) {
      this.select(visibleIndex);
      return;
    }

    this.customDate = date;
    this.selectedKey = key;

    this.daysContainer.querySelectorAll('.delivery-day').forEach((button) => {
      button.setAttribute('aria-pressed', 'false');
    });
    this.calendarTile?.setAttribute('aria-pressed', 'true');

    if (this.input) this.input.value = this.formatFull(date);
    if (this.note) {
      let text = `Lieferung am <strong>${this.formatFull(date)}</strong>.`;
      if (sundayMoved) {
        text = `Sonntags liefern wir nicht, dein Liefertag ist der <strong>${this.formatFull(date)}</strong>.`;
      }
      this.note.innerHTML = text;
      this.note.hidden = false;
    }
  }

  updateNote(isEarliest) {
    if (!this.note) return;
    const date = this.dates.find((d) => this.formatKey(d) === this.selectedKey);
    if (!date) return;

    let text = `Lieferung am <strong>${this.formatShort(date)}</strong>`;
    if (isEarliest) {
      const minutesLeft = Math.max(1, Math.round((this.deadline - new Date()) / 60000));
      const hours = Math.floor(minutesLeft / 60);
      const minutes = minutesLeft % 60;
      const parts = [];
      if (hours > 0) parts.push(`${hours} ${hours === 1 ? 'Stunde' : 'Stunden'}`);
      parts.push(`${minutes} ${minutes === 1 ? 'Minute' : 'Minuten'}`);
      text += `, wenn du innerhalb von <strong>${parts.join(' ')}</strong> bestellst.`;
    }
    this.note.innerHTML = text;
    this.note.hidden = false;
  }

  tick() {
    const earliestKey = this.formatKey(this.deliveryDays[0]);
    const renderedKey = this.dates ? this.formatKey(this.dates[0]) : null;
    if (earliestKey !== renderedKey) {
      this.render();
      return;
    }
    this.updateNote(this.selectedKey === renderedKey);
  }
}

if (!customElements.get('delivery-picker')) {
  customElements.define('delivery-picker', DeliveryPicker);
}

/* --------------------------------------------------------------------------
 * Shipping Picker – Lieferart-Karten mit Auswahlzustand
 * ------------------------------------------------------------------------ */

class ShippingPicker extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-shipping-input]');
    this.options = Array.from(this.querySelectorAll('[data-shipping-option]'));

    this.options.forEach((option) => {
      option.addEventListener('click', () => this.select(option));
    });

    const preselected = this.options.find(
      (option) => option.getAttribute('aria-pressed') === 'true'
    );
    if (preselected) this.select(preselected);
  }

  select(selected) {
    this.options.forEach((option) => {
      option.setAttribute('aria-pressed', option === selected ? 'true' : 'false');
    });
    if (this.input) this.input.value = selected.dataset.value || '';
  }
}

if (!customElements.get('shipping-picker')) {
  customElements.define('shipping-picker', ShippingPicker);
}

/* --------------------------------------------------------------------------
 * Collapsible Text – gekürzte Beschreibung mit "Mehr anzeigen"
 * ------------------------------------------------------------------------ */

class CollapsibleText extends HTMLElement {
  connectedCallback() {
    this.content = this.querySelector('[data-content]');
    this.toggle = this.querySelector('[data-toggle]');
    if (!this.content || !this.toggle) return;

    this.toggle.addEventListener('click', () => {
      const isOpen = this.classList.toggle('is-open');
      this.querySelector('[data-label-more]').hidden = isOpen;
      this.querySelector('[data-label-less]').hidden = !isOpen;
    });

    requestAnimationFrame(() => {
      if (this.content.scrollHeight > this.content.clientHeight + 2) {
        this.toggle.hidden = false;
      }
    });
  }
}

if (!customElements.get('collapsible-text')) {
  customElements.define('collapsible-text', CollapsibleText);
}

/* --------------------------------------------------------------------------
 * Product Recommendations – lädt "Ähnliche Produkte" über die
 * Empfehlungs-API nach, sobald die Section sichtbar wird.
 * ------------------------------------------------------------------------ */

class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        observer.disconnect();
        this.load();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    observer.observe(this);
  }

  async load() {
    try {
      const response = await fetch(this.dataset.url);
      const text = await response.text();
      const html = new DOMParser().parseFromString(text, 'text/html');
      const recommendations = html.querySelector('product-recommendations');
      if (recommendations?.innerHTML.trim()) {
        this.innerHTML = recommendations.innerHTML;
      }
    } catch (error) {
      console.error('[product] recommendations failed', error);
    }
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
